import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type NoticeTone = 'warning' | 'danger';

interface NoticeProps {
  tone: NoticeTone;
  title?: string;
  /** Shown before the text; the compact strip always wants one. */
  icon?: LucideIcon;
  children?: ReactNode;
  /** Right-aligned control, e.g. a Retry button. */
  action?: ReactNode;
  /** One-line strip (e.g. "3 assets have warranty expiring") instead of a titled block. */
  compact?: boolean;
  className?: string;
}

const TONES: Record<NoticeTone, string> = {
  warning:
    'border-amber-300/70 bg-amber-50/70 text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-300',
  danger: 'border-destructive/30 bg-destructive/5 text-destructive',
};

/**
 * Inline page/section notice — a setup problem, a warning, or a load failure. Not a toast:
 * it stays on screen for as long as the condition holds.
 */
export function Notice({ tone, title, icon: Icon, children, action, compact = false, className }: NoticeProps) {
  if (compact) {
    return (
      <div className={cn('flex items-center gap-3 rounded-lg border px-4 py-3', TONES[tone], className)}>
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="flex-1 text-sm font-medium">{children}</span>
        {action}
      </div>
    );
  }
  return (
    <div className={cn('flex gap-3 rounded-lg border p-4 text-sm', TONES[tone], className)}>
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" />}
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && 'mt-1')}>{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
